/**
 * Radiators.co.uk BTU calculator (Shopify rebuild). Port of calculator/btu.py, the reference
 * implementation of the live Cost Calculator Builder calc 68462 (docs/08-search-filter-calculator-spec.md).
 *
 * Live formula, feet in:  Math.round(L * W * H * room * south * glazing)
 *   room factors: Bedroom 4.5, Kitchen 3.5, Bathroom 5, Living Room 5, Dining Room 3.5, Hallway 3
 *   south-facing: yes 0.85, no 1; double glazed: yes 0.9, no 1; an unselected choice contributes 0.
 * The expression is evaluated in IEEE-754 doubles in the same order as the live code, so results,
 * including the rare exact .5 ties, equal the live calculator. Metric input (approved by Nathan,
 * decision N-06) converts metres to feet with the exact factor 0.3048 m per ft before the formula.
 *
 * Pure functions are exported for the parity test (tests/theme/btu_parity.mjs). The browser part
 * enhances every [data-btu-calculator] element; it is loaded as a deferred module and imports nothing.
 * "Use my BTU estimate" links to the finder with ?btu=<estimate> (buildFinderHandoffUrl).
 * Analytics: CustomEvents calculator_start, calculator_complete and calculator_result_click bubble
 * from the calculator root with a detail object that holds no personal data and no raw dimensions.
 */

export const M_PER_FT = 0.3048;
export const MAX_FT = 100;
export const MAX_M = 30.48;
export const DEFAULT_ROOMS = Object.freeze([
  { id: 'bedroom', label: 'Bedroom', factor: 4.5 },
  { id: 'kitchen', label: 'Kitchen', factor: 3.5 },
  { id: 'bathroom', label: 'Bathroom', factor: 5 },
  { id: 'living-room', label: 'Living Room', factor: 5 },
  { id: 'dining-room', label: 'Dining Room', factor: 3.5 },
  { id: 'hallway', label: 'Hallway', factor: 3 },
]);
export const SOUTH_FACTORS = Object.freeze({ yes: 0.85, no: 1 });
export const GLAZED_FACTORS = Object.freeze({ yes: 0.9, no: 1 });

export class CalculatorInputError extends Error {}

const NUMBER_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

/** Parse one dimension. Empty means 0 (as the live calculator); anything not a plain decimal throws. */
export function parseDimension(text, name = 'value') {
  if (text === null || text === undefined) return 0;
  if (typeof text === 'number') {
    if (!Number.isFinite(text)) throw new CalculatorInputError(`${name}: not finite`);
    return text;
  }
  const s = String(text).trim();
  if (s === '') return 0;
  if (!NUMBER_RE.test(s)) throw new CalculatorInputError(`${name}: not a number`);
  const n = Number(s);
  if (!Number.isFinite(n)) throw new CalculatorInputError(`${name}: not finite`);
  return n;
}

export function metresToFeet(m) {
  return m / M_PER_FT;
}

/**
 * Same semantics as calculator/btu.py estimate_btu. south / glazed: true, false or null (unselected).
 * roomFactor: a number, or null when no room is selected. Dimensions in feet (strings or numbers).
 * Returns { btu, volumeFt3, valid, problems } with problems as codes in the Python order.
 */
export function estimateBtu(lengthFt, widthFt, heightFt, roomFactor, south, glazed, options = {}) {
  const max = options.max ?? MAX_FT;
  const raw = options.raw ?? { length: lengthFt, width: widthFt, height: heightFt };
  const l = parseDimension(lengthFt, 'length');
  const w = parseDimension(widthFt, 'width');
  const h = parseDimension(heightFt, 'height');
  const problems = [];
  let r = 0;
  if (roomFactor === null || roomFactor === undefined) problems.push('room_required');
  else r = Number(roomFactor);
  let s = 0;
  if (south === null || south === undefined) problems.push('south_required');
  else s = south ? SOUTH_FACTORS.yes : SOUTH_FACTORS.no;
  let g = 0;
  if (glazed === null || glazed === undefined) problems.push('glazed_required');
  else g = glazed ? GLAZED_FACTORS.yes : GLAZED_FACTORS.no;
  for (const [name, value] of [['length', raw.length], ['width', raw.width], ['height', raw.height]]) {
    const v = parseDimension(value, name);
    if (v <= 0) problems.push(`${name}_required`);
    else if (v > max) problems.push(`${name}_max`);
  }
  const volume = l * w * h; // total_field_id_7, cubic feet
  const x = volume * r * s * g; // total_field_id_6 before rounding
  let btu = Math.round(x);
  if (Number.isNaN(btu) || btu === -Infinity) btu = 0; // validateTotal
  return { btu, volumeFt3: volume, valid: problems.length === 0, problems };
}

/** Metric entry: metres are converted to feet first; the range check stays in metres (0 < m <= 30.48). */
export function estimateBtuMetric(lengthM, widthM, heightM, roomFactor, south, glazed) {
  const l = metresToFeet(parseDimension(lengthM, 'length'));
  const w = metresToFeet(parseDimension(widthM, 'width'));
  const h = metresToFeet(parseDimension(heightM, 'height'));
  return estimateBtu(l, w, h, roomFactor, south, glazed, {
    max: MAX_M,
    raw: { length: lengthM, width: widthM, height: heightM },
  });
}

/** Non-overlapping inclusive band [k*width, k*width + width - 1] (btu.py proposed_band). */
export function outputBand(value, width = 250) {
  if (value < 0 || width <= 0) throw new CalculatorInputError('value must be >= 0 and width > 0');
  const lo = Math.floor(value / width) * width;
  return [lo, lo + width - 1];
}

/**
 * Gate B calculator target band (docs/search/gate-b-search-and-filtering.md 6.4, search/bands.py
 * calculator_target_band): the 250 BTU landing band whose lower bound is the estimate rounded UP to a
 * multiple of the width, so every product in it lists an output at or above the estimate (as published).
 */
export function targetBand(value, width = 250) {
  if (value < 0 || width <= 0) throw new CalculatorInputError('value must be >= 0 and width > 0');
  const lo = Math.ceil(value / width) * width;
  return [lo, lo + width - 1];
}

export function buildResultUrl(pattern, btu, band) {
  return String(pattern)
    .replaceAll('{btu}', String(btu))
    .replaceAll('{band_min}', String(band[0]))
    .replaceAll('{band_max}', String(band[1]));
}

/** "Use my BTU estimate": the finder page with the estimate as ?btu= (read by assets/rad-finder.js). */
export function buildFinderHandoffUrl(finderUrl, btu) {
  const base = String(finderUrl);
  return `${base}${base.includes('?') ? '&' : '?'}btu=${Math.max(0, Math.round(Number(btu)) || 0)}`;
}

// ------------------------------------------------------------------------------------------------
// Browser enhancement
// ------------------------------------------------------------------------------------------------

const nf = (digits = 0) =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits: digits, minimumFractionDigits: 0 });

function fill(template, values) {
  return String(template).replace(/\{(\w+)\}/g, (m, k) => (k in values ? values[k] : m));
}

/** sessionStorage key for the last calculator inputs (never the URL; GA4 plan section 4.2). */
export const STORAGE_KEY = 'rad-btu-calculator';
export const DIMENSION_PARAMS = Object.freeze(['btu_u', 'btu_l', 'btu_w', 'btu_h', 'btu_r', 'btu_s', 'btu_g']);

/** The same URL with every calculator parameter removed (other parameters and the fragment kept). */
export function withoutDimensionParams(href) {
  const url = new URL(href);
  for (const k of DIMENSION_PARAMS) url.searchParams.delete(k);
  return url.toString();
}

function radioValue(form, name) {
  const el = form.querySelector(`input[name="${name}"]:checked`);
  if (!el) return null;
  return el.value === 'yes';
}

class BtuCalculator {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('form');
    this.config = JSON.parse(root.querySelector('script[data-btu-config]').textContent);
    this.result = root.querySelector('[data-btu-result]');
    this.started = false;
    this.lastBand = null;
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.calculate(true);
    });
    this.form.addEventListener('input', () => this.start(), { once: false });
    this.form.addEventListener('change', (e) => {
      this.start();
      if (e.target.name === 'unit') this.updateUnit();
      if (this.hasResult) this.calculate(false);
    });
    this.root.addEventListener('click', (e) => {
      const a = e.target.closest('a[data-btu-result-link]');
      if (a) this.emit('calculator_result_click', { output_band: this.lastBand, target: 'collection' });
      if (e.target.closest('a[data-btu-finder-link]')) this.emit('calculator_result_click', { output_band: this.lastBand, target: 'finder' });
    });
    this.restore();
    this.updateUnit();
    // Astra TL-02: the submit button ships disabled and is enabled only now that the submit handler is attached
    const submit = this.form.querySelector('[data-btu-submit]');
    if (submit) submit.disabled = false;
  }

  emit(name, detail) {
    this.root.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
  }

  start() {
    if (this.started) return;
    this.started = true;
    this.emit('calculator_start', { room_type: this.roomId(), unit: this.unit() });
  }

  unit() {
    return this.form.querySelector('input[name="unit"]:checked')?.value === 'm' ? 'm' : 'ft';
  }

  /** A named text input or select. Never form.elements: form.elements.length is the control count. */
  field(name) {
    return this.form.querySelector(`input[name="${name}"]:not([type="radio"]), select[name="${name}"]`);
  }

  roomId() {
    const v = this.field('room').value;
    return v === '' ? null : (this.config.rooms[Number(v)]?.id ?? null);
  }

  updateUnit() {
    const u = this.unit();
    for (const el of this.root.querySelectorAll('[data-unit-label]')) {
      el.textContent = u === 'm' ? this.config.strings.unit_m_short : this.config.strings.unit_ft_short;
    }
    for (const name of ['length', 'width', 'height']) {
      const input = this.field(name);
      input.max = u === 'm' ? String(MAX_M) : String(MAX_FT);
      input.step = 'any';
    }
  }

  setError(name, code) {
    const input = this.field(name);
    const err = this.root.querySelector(`[data-error-for="${name}"]`);
    const group = this.root.querySelector(`[data-group="${name}"]`);
    const msg = code ? this.config.strings[`error_${code}`] || this.config.strings.error_required : '';
    if (err) {
      err.textContent = msg;
      err.hidden = !msg;
    }
    const target = input || group;
    if (target) {
      if (msg) target.setAttribute('aria-invalid', 'true');
      else target.removeAttribute('aria-invalid');
    }
  }

  /**
   * GA4 plan 4.2 follow-up (2026-10-26): room dimensions never go into the URL. The last inputs are kept in
   * sessionStorage (this tab only, never sent to any server or tracker). A legacy link that still carries btu_*
   * parameters is restored once (layout/theme.liquid moves them into the same storage key before trackers load) and
   * the parameters are removed from the address bar at once.
   */
  restore() {
    const legacy = new URLSearchParams(window.location.search);
    let p = null;
    // Astra TL-05: every calculator parameter is removed whenever any is present (layout/theme.liquid does the same
    // before content_for_header; this is the second line for pages that skipped it)
    const clean = withoutDimensionParams(window.location.href);
    if (clean !== window.location.href) {
      try {
        window.history.replaceState(window.history.state, '', clean);
      } catch {
        // history blocked: the calculator still works; the GA4 URL rebuild (plan 4.2) remains the control
      }
    }
    if (legacy.has('btu_l')) {
      p = legacy;
    } else {
      try {
        const saved = window.sessionStorage.getItem(STORAGE_KEY);
        if (saved) p = new URLSearchParams(saved);
      } catch {
        p = null;
      }
    }
    if (!p || !p.has('btu_l')) return;
    const f = this.form;
    if (p.get('btu_u') === 'm') f.querySelector('input[name="unit"][value="m"]').checked = true;
    this.field('length').value = p.get('btu_l') || '';
    this.field('width').value = p.get('btu_w') || '';
    this.field('height').value = p.get('btu_h') || '';
    const room = this.config.rooms.findIndex((r) => r.id === p.get('btu_r'));
    if (room >= 0) this.field('room').value = String(room);
    for (const [k, name] of [['btu_s', 'south'], ['btu_g', 'glazed']]) {
      const v = p.get(k);
      const el = f.querySelector(`input[name="${name}"][value="${v === '1' ? 'yes' : 'no'}"]`);
      if (v !== null && el) el.checked = true;
    }
    this.calculate(false, false);
  }

  persist(unit, room) {
    const f = this.form;
    const state = new URLSearchParams();
    state.set('btu_u', unit);
    state.set('btu_l', this.field('length').value.trim());
    state.set('btu_w', this.field('width').value.trim());
    state.set('btu_h', this.field('height').value.trim());
    state.set('btu_r', room?.id ?? '');
    state.set('btu_s', radioValue(f, 'south') ? '1' : '0');
    state.set('btu_g', radioValue(f, 'glazed') ? '1' : '0');
    try {
      window.sessionStorage.setItem(STORAGE_KEY, state.toString());
    } catch {
      // storage blocked (private mode, policy): the result still shows, it is just not kept for a reload
    }
  }

  calculate(fromSubmit, emit = true) {
    const f = this.form;
    const S = this.config.strings;
    const unit = this.unit();
    const idx = this.field('room').value;
    const room = idx === '' ? null : this.config.rooms[Number(idx)];
    const south = radioValue(f, 'south');
    const glazed = radioValue(f, 'glazed');
    const dims = ['length', 'width', 'height'].map((n) => this.field(n).value);
    for (const n of ['length', 'width', 'height', 'room', 'south', 'glazed']) this.setError(n, null);

    let r;
    const bad = [];
    for (const [i, n] of ['length', 'width', 'height'].entries()) {
      try {
        parseDimension(dims[i], n);
      } catch {
        bad.push(n);
        this.setError(n, 'number');
      }
    }
    if (bad.length === 0) {
      r = unit === 'm'
        ? estimateBtuMetric(dims[0], dims[1], dims[2], room ? room.factor : null, south, glazed)
        : estimateBtu(dims[0], dims[1], dims[2], room ? room.factor : null, south, glazed);
      for (const code of r.problems) {
        const [field, kind] = code.split('_');
        this.setError(field, kind === 'max' ? (unit === 'm' ? 'max_m' : 'max_ft') : 'required');
      }
    }
    if (bad.length || !r.valid) {
      this.hasResult = false;
      this.result.innerHTML = '';
      const p = document.createElement('p');
      p.className = 'btu-calc__result-prompt';
      p.textContent = S.fix_errors;
      this.result.append(p);
      if (fromSubmit) {
        const first = this.root.querySelector('[aria-invalid="true"]');
        const focusable = first?.matches('input, select') ? first : first?.querySelector('input');
        focusable?.focus();
      }
      return;
    }

    this.hasResult = true;
    const band = outputBand(Math.max(r.btu, 0), this.config.band_width || 250);
    this.lastBand = `${band[0]}-${band[1]}`;
    const exact = r.volumeFt3 * room.factor * (south ? SOUTH_FACTORS.yes : 1) * (glazed ? GLAZED_FACTORS.yes : 1);
    const volM3 = r.volumeFt3 * M_PER_FT ** 3;
    const parts = [
      fill(S.explain_volume, { ft3: nf(1).format(r.volumeFt3), m3: nf(1).format(volM3) }),
      fill(S.explain_room, { room: room.label, factor: nf(2).format(room.factor) }),
    ];
    if (south) parts.push(fill(S.explain_south, { factor: nf(2).format(SOUTH_FACTORS.yes) }));
    if (glazed) parts.push(fill(S.explain_glazed, { factor: nf(2).format(GLAZED_FACTORS.yes) }));

    const wrap = document.createElement('div');
    const heading = document.createElement('p');
    heading.className = 'btu-calc__result-label';
    heading.textContent = S.result_label;
    const value = document.createElement('p');
    value.className = 'btu-calc__result-value';
    value.textContent = fill(S.result_value, { btu: nf(0).format(r.btu) });
    const explain = document.createElement('p');
    explain.className = 'btu-calc__result-explain';
    explain.textContent = `${parts.join(' ')} ${fill(S.explain_total, { exact: nf(2).format(exact), btu: nf(0).format(r.btu) })}`;
    const note = document.createElement('p');
    note.className = 'btu-calc__result-note';
    note.textContent = S.delta_note;
    wrap.append(heading, value, explain, note);
    if (this.config.result_url_pattern) {
      const a = document.createElement('a');
      a.className = 'button btu-calc__result-link';
      const target = targetBand(Math.max(r.btu, 0), this.config.band_width || 250);
      a.href = buildResultUrl(this.config.result_url_pattern, r.btu, target);
      a.rel = 'nofollow';
      a.dataset.btuResultLink = '';
      a.textContent = fill(S.result_link, {
        btu: nf(0).format(r.btu),
        band_min: nf(0).format(target[0]),
        band_max: nf(0).format(target[1]),
      });
      wrap.append(a);
    }
    if (this.config.finder_url && r.btu > 0) {
      const f = document.createElement('a');
      f.className = 'btu-calc__finder-link';
      f.href = buildFinderHandoffUrl(this.config.finder_url, r.btu);
      f.rel = 'nofollow';
      f.dataset.btuFinderLink = '';
      f.textContent = S.use_in_finder;
      wrap.append(f);
    }
    this.result.replaceChildren(wrap);
    this.persist(unit, room);
    if (emit) {
      this.emit('calculator_complete', {
        room_type: room.id,
        south_facing: south,
        double_glazed: glazed,
        output_band: this.lastBand,
        unit,
      });
    }
  }
}

if (typeof document !== 'undefined' && typeof customElements !== 'undefined') {
  const boot = () => {
    for (const root of document.querySelectorAll('[data-btu-calculator]:not([data-btu-ready])')) {
      root.dataset.btuReady = '';
      try {
        new BtuCalculator(root);
      } catch (err) {
        console.error('BTU calculator failed to start', err);
      }
    }
  };
  boot();
  document.addEventListener('shopify:section:load', boot);
}
